import { useState, useRef, useEffect, ReactNode } from 'react';
import { FormikValues, getIn } from 'formik';
import formatPhoneNumber from 'utils/formatPhoneNumber';
import isPhoneNumberPartiallyFilled from 'utils/isPhoneNumberPartiallyFilled';
import isPhoneNumberValid from 'utils/validation/isPhoneNumberValid';
import { debounce } from 'lodash';
import { useTranslator } from 'translation/useTranslator';

import { Button, TextField } from '@nps.today/component-library';

const dataTestIdSuffix = 'phone-number';

type PhoneNumberInputFieldProps = {
  fieldName: string;
  label?: string;
  formik: FormikValues;
  initialValue?: string;
  disabled?: boolean;
  endAdornment?: ReactNode;
  required?: boolean;
  dataTestId: string;
};
const PhoneNumberInputField = ({
  fieldName,
  label = 'Phone',
  formik,
  initialValue,
  disabled = false,
  endAdornment,
  required,
  dataTestId
}: PhoneNumberInputFieldProps) => {
  const translator = useTranslator();
  const [enforceCountryCode, setEnforceCountryCode] = useState(true);
  const [inputValue, setInputValue] = useState(() => formik.getFieldProps(fieldName).value || '');
  const isFocusedRef = useRef(false);
  useEffect(() => {
    //if the initial value is empty or a valid phonenumber, enforce the country code
    setEnforceCountryCode(!initialValue?.length || isPhoneNumberValid(initialValue));
  }, [initialValue]);

  const { setFieldValue, errors } = formik;
  const fieldValue = formik.getFieldProps(fieldName).value || '';

  const hasCountryCode = (phoneNumber: string) => {
    return phoneNumber.startsWith('+');
  };

  const debouncedPhoneNumberUpdate = useRef(
    debounce((phoneNumber?: string) => {
      setFieldValue(fieldName, phoneNumber ?? '', false);
    }, 300)
  ).current;

  useEffect(() => {
    if (!isFocusedRef.current && fieldValue !== inputValue) {
      setInputValue(fieldValue);
    }
  }, [fieldValue, inputValue]);

  useEffect(() => {
    return () => {
      debouncedPhoneNumberUpdate.cancel();
    };
  }, [debouncedPhoneNumberUpdate]);

  const getHelperText = () => {
    const value = inputValue || fieldValue;
    return value && !hasCountryCode(value) && Boolean(errors[fieldName]) ? (
      <>
        {translator.phoneWithoutCountryCode(value)}
        {isPhoneNumberValid('+' + value) && (
          <Button
            dataTestId={`${dataTestId}-${dataTestIdSuffix}-format-number-button`}
            onClick={() => {
              setFieldValue(fieldName, formatPhoneNumber('+' + value));
            }}
          >
            {translator.useFormattedNumber(formatPhoneNumber('+' + value))}
          </Button>
        )}
      </>
    ) : (
      errors[fieldName]
    );
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    isFocusedRef.current = false;
    const newPhoneNumber = event.target.value;
    let formattedPhoneNumber = '';

    if (newPhoneNumber.length) {
      if (isPhoneNumberPartiallyFilled(newPhoneNumber)) {
        formattedPhoneNumber = formatPhoneNumber(newPhoneNumber);
      } else {
        formattedPhoneNumber = newPhoneNumber;
      }
    }

    debouncedPhoneNumberUpdate.cancel();
    setInputValue(formattedPhoneNumber);
    setFieldValue(fieldName, formattedPhoneNumber);
  };

  return (
    <>
      {enforceCountryCode ? (
        <TextField
          dataTestId={`${dataTestId}-${dataTestIdSuffix}-country-code-enforced-text-field`}
          disabled={disabled}
          key={fieldName}
          value={inputValue}
          fullWidth
          label={label}
          name={fieldName}
          variant="outlined"
          required={required}
          onFocus={() => {
            isFocusedRef.current = true;
          }}
          onChange={(event) => {
            const newPhoneNumber = event.target.value;
            setInputValue(newPhoneNumber);
            //update form with entered value
            debouncedPhoneNumberUpdate(newPhoneNumber);
          }}
          onBlur={handleBlur}
          error={Boolean(errors[fieldName])}
          helperText={getHelperText()}
          InputProps={{ endAdornment: endAdornment }}
          inputProps={{
            'data-testid': `${dataTestId}-${dataTestIdSuffix}-country-code-enforced-text-field`,
            inputMode: 'tel',
            autoComplete: 'tel'
          }}
        />
      ) : (
        <TextField
          dataTestId={`${dataTestId}-${dataTestIdSuffix}-standard`}
          label={label}
          fullWidth
          {...formik.getFieldProps(fieldName)}
          error={Boolean(getIn(formik.errors, fieldName))}
          helperText={getHelperText()}
        />
      )}
    </>
  );
};

export default PhoneNumberInputField;
